// Phase 4 · M4 · Conferences client library.
//
// Two-table model: `conference_slots` (teacher availability) and
// `conference_bookings` (parent claims). All read fns tolerate 42P01.

import { supabase, isSupabaseConfigured } from "@/lib/supabase";

const SLOT_SELECT = `
  id, organization_id, teacher_profile_id, classroom_id,
  start_at, end_at, location, notes, status, created_at, updated_at
`;
const SLOT_WITH_TEACHER = `${SLOT_SELECT},
  teacher:profiles!conference_slots_teacher_profile_id_fkey(id, full_name, email),
  classroom:classrooms(id, name)
`;
const BOOKING_SELECT = `
  id, organization_id, slot_id, child_id, family_id, parent_profile_id,
  notes, status, cancelled_at, created_at, updated_at
`;

// ---------- slots ----------

export async function listOpenSlotsForClassroom(classroomId, { sinceIso } = {}) {
  if (!isSupabaseConfigured || !classroomId) return [];
  const cutoff = sinceIso || new Date().toISOString();
  const { data, error } = await supabase
    .from("conference_slots")
    .select(SLOT_WITH_TEACHER)
    .eq("classroom_id", classroomId)
    .eq("status", "open")
    .gt("start_at", cutoff)
    .order("start_at", { ascending: true });
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function listMyTeacherSlots({ teacherProfileId, sinceIso }) {
  if (!isSupabaseConfigured || !teacherProfileId) return [];
  const cutoff = sinceIso || new Date(Date.now() - 24 * 3600 * 1000).toISOString();
  const { data, error } = await supabase
    .from("conference_slots")
    .select(`${SLOT_WITH_TEACHER},
      bookings:conference_bookings(id, status, child:children(id, first_name, last_name))
    `)
    .eq("teacher_profile_id", teacherProfileId)
    .gte("start_at", cutoff)
    .order("start_at", { ascending: true });
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function listOrgSlotsForDateRange({ fromIso, toIso }) {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from("conference_slots")
    .select(`${SLOT_WITH_TEACHER},
      bookings:conference_bookings(id, status, child:children(id, first_name, last_name), family:families(id, display_name))
    `)
    .gte("start_at", fromIso)
    .lte("start_at", toIso)
    .order("start_at", { ascending: true });
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function createSlot({
  organizationId, teacherProfileId, classroomId,
  startAt, endAt, location, notes,
}) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase
    .from("conference_slots")
    .insert({
      organization_id: organizationId,
      teacher_profile_id: teacherProfileId,
      classroom_id: classroomId,
      start_at: startAt,
      end_at: endAt,
      location: location?.trim() || null,
      notes: notes?.trim() || null,
    })
    .select(SLOT_SELECT)
    .single();
  if (error) throw error;
  return data;
}

/** Bulk-insert slots from an array of {startAt, endAt} pairs. */
export async function createSlotsBulk(rows) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  if (!rows?.length) return [];
  const { data, error } = await supabase
    .from("conference_slots")
    .insert(rows)
    .select(SLOT_SELECT);
  if (error) throw error;
  return data || [];
}

export async function cancelSlot(id) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase
    .from("conference_slots")
    .update({ status: "cancelled" })
    .eq("id", id)
    .select(SLOT_SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteSlot(id) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { error } = await supabase.from("conference_slots").delete().eq("id", id);
  if (error) throw error;
}

// ---------- bookings ----------

export async function listMyFamilyBookings({ familyId }) {
  if (!isSupabaseConfigured || !familyId) return [];
  const { data, error } = await supabase
    .from("conference_bookings")
    .select(`${BOOKING_SELECT},
      slot:conference_slots(id, start_at, end_at, location, notes, status,
        teacher:profiles!conference_slots_teacher_profile_id_fkey(id, full_name),
        classroom:classrooms(id, name)),
      child:children(id, first_name, last_name)
    `)
    .eq("family_id", familyId)
    .order("created_at", { ascending: false });
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function bookSlot({ organizationId, slotId, childId, familyId, parentProfileId, notes }) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase
    .from("conference_bookings")
    .insert({
      organization_id: organizationId,
      slot_id: slotId,
      child_id: childId,
      family_id: familyId,
      parent_profile_id: parentProfileId,
      notes: notes?.trim() || null,
    })
    .select(BOOKING_SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function cancelBooking(id) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase
    .from("conference_bookings")
    .update({ status: "cancelled", cancelled_at: new Date().toISOString() })
    .eq("id", id)
    .select(BOOKING_SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function teacherMarkBooking({ id, status }) {
  if (!["completed", "no_show"].includes(status)) throw new Error("Invalid status");
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase
    .from("conference_bookings")
    .update({ status })
    .eq("id", id)
    .select(BOOKING_SELECT)
    .single();
  if (error) throw error;
  return data;
}

// ---------- helpers ----------

export function formatSlotRange(startIso, endIso) {
  if (!startIso) return "";
  const s = new Date(startIso);
  const e = endIso ? new Date(endIso) : null;
  const day = s.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const fmt = (d) => d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return e ? `${day} · ${fmt(s)}–${fmt(e)}` : `${day} · ${fmt(s)}`;
}

export function durationMinutes(startIso, endIso) {
  return Math.round((new Date(endIso) - new Date(startIso)) / 60000);
}

/**
 * Generate slot rows for a date range from a daily template:
 * For each calendar day in [startDate, endDate], create slots between
 * dayStart and dayEnd of `slotMinutes` minutes, skipping any range in
 * `breaks` (array of {start, end} in HH:MM).
 */
export function expandTemplate({
  startDate, endDate, dayStart, dayEnd, slotMinutes, breaks = [], days = [1,2,3,4,5],
  organizationId, teacherProfileId, classroomId, location,
}) {
  const out = [];
  const cur = new Date(`${startDate}T00:00:00`);
  const stop = new Date(`${endDate}T00:00:00`);
  while (cur <= stop) {
    if (days.includes(cur.getDay())) {
      const dayStr = cur.toISOString().slice(0, 10);
      const [sh, sm] = dayStart.split(":").map(Number);
      const [eh, em] = dayEnd.split(":").map(Number);
      const dayStartMs = new Date(`${dayStr}T${dayStart}:00`);
      const dayEndMs = new Date(`${dayStr}T${dayEnd}:00`);
      let cursor = new Date(dayStartMs);
      while (cursor.getTime() + slotMinutes * 60000 <= dayEndMs.getTime()) {
        const slotEnd = new Date(cursor.getTime() + slotMinutes * 60000);
        const inBreak = breaks.some((b) => {
          const bs = new Date(`${dayStr}T${b.start}:00`);
          const be = new Date(`${dayStr}T${b.end}:00`);
          return cursor < be && slotEnd > bs;
        });
        if (!inBreak) {
          out.push({
            organization_id: organizationId,
            teacher_profile_id: teacherProfileId,
            classroom_id: classroomId,
            start_at: cursor.toISOString(),
            end_at: slotEnd.toISOString(),
            location: location || null,
          });
        }
        cursor = slotEnd;
      }
    }
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}
